import { TokenSymbol } from '@nsfw-app/crypto'
import { getTokensForChainId } from 'config/tokens'
import { PaymentOptions } from 'containers/PaymentModal/types'
import { BigNumber, Contract, Signer, ethers, providers } from 'ethers'
import { PaymentStatus } from 'graphql/types'
import { makeAutoObservable } from 'mobx'
import { toast } from 'components/common/Toast'
import { EthAddress } from './InjectedWallet'
import { RootStore } from './RootStore'
import { TransactionCTA, TransactionStatus } from './Transaction'

export class ERC20Contract {
  root: RootStore

  constructor(root: RootStore) {
    this.root = root
    makeAutoObservable(this)
  }

  /**
   * Returns an ethers Contract with a Signer attached
   * @param tokenSymbol
   */
  async getContractForToken(tokenSymbol: TokenSymbol): Promise<{
    signerAddress: string
    provider: providers.Web3Provider
    signer: Signer
    token: Contract
  }> {
    const provider = await this.root.wallets.resolveProvider()
    if (!provider) throw new Error('Unable to resolve provider')

    const signerAddress = this.root.wallets.connected?.address
    if (!signerAddress) throw new Error('No signer address found')

    const signer = provider.getSigner()
    const token = getTokensForChainId(this.root.injectedWallet.selectedNetwork.id).find(
      (n) => n.symbol === tokenSymbol
    )
    if (!token) throw new Error('Token not found')
    const contract = new Contract(token.contract!, token.abi, signer)

    return {
      signerAddress,
      provider,
      signer,
      token: contract
    }
  }

  /**
   * Returns signers token balance
   */
  async getTokenBalance(tokenSymbol: TokenSymbol): Promise<ethers.BigNumber> {
    const { token, signerAddress } = await this.getContractForToken(tokenSymbol)

    try {
      const balance = await token.balanceOf(signerAddress)
      return balance
    } catch (err) {
      console.error('unable to get token balance', err)
      return ethers.BigNumber.from(0)
    }
  }

  /**
   * Send the transaction to the blockchain
   */
  async transfer() {
    const provider = await this.root.wallets.resolveProvider()
    if (!provider) throw new Error('Unable to resolve provider')

    const order = this.root.transaction.order
    if (!order) throw new Error('No current transaction order found')

    const contract = this.root.transaction.contract
    const { gasLimit, gasPrice } = this.root.transaction.transferEstimate || {}
    if (!(contract && gasLimit && gasPrice)) throw new Error('No transfer estimate available')

    console.debug('Attempting transfer')

    this.root.transaction.setCheckoutState({
      isProcessing: true,
      buttonLabel: TransactionCTA.PENDING_USER_CONFIRMATION,
      status: TransactionStatus.PENDING_USER_CONFIRMATION
    })

    try {
      const rawTxn = await contract.populateTransaction.transfer(
        order.recipient,
        this.root.transaction.formattedTokens._hex,
        {
          gasPrice: gasPrice,
          gasLimit: gasLimit
        }
      )

      const transactionHash = await provider.send('eth_sendTransaction', [
        {
          from: rawTxn.from,
          to: rawTxn.to,
          data: rawTxn.data,
          gas: rawTxn.gasLimit?._hex,
          gasPrice: rawTxn.gasPrice?._hex,
          value: rawTxn.value?._hex
        }
      ])

      try {
        const paymentMutationsInput = {
          orderId: order.orderId,
          transactionHash: transactionHash
        }
        await this.root.transaction.recordPayment(paymentMutationsInput)

        this.root.transaction.setCheckoutState({
          isProcessing: true,
          buttonLabel: TransactionCTA.PROCESSING,
          status: TransactionStatus.SENDING_TOKENS,
          transactionHash: transactionHash
        })

        provider.on('poll', this.root.transaction.setNumberOfConfirmations)
        await provider.waitForTransaction(transactionHash, 1)

        /**
         * !!! IMPORTANT !!!
         * - must be awaited before completing checkout
         * - otherwise post.isVisible is false (specifically confirmPayment)
         */
        const payment = await this.root.transaction.confirmPayment(paymentMutationsInput)

        if (payment?.status !== PaymentStatus.FLAGGED) {
          if (this.root.transaction.paymentType === PaymentOptions.MESSAGE_PACK) {
            this.root.user.fetchMessageCredits()
          }

          if (this.root.transaction.paymentType === PaymentOptions.SUBSCRIBE_PROFILE) {
            this.root.user.fetchSubscribersList()
          }
        }

        this.root.transaction.setCheckoutState({
          isProcessing: false,
          isCompleted: true,
          status:
            payment?.status === PaymentStatus.FLAGGED
              ? TransactionStatus.FLAGGED
              : TransactionStatus.CONFIRMED,
          buttonLabel: TransactionCTA.CONFIRMED,
          numberOfConfirmations: this.root.transaction.REQUIRED_CONFIRMATIONS
        })
      } catch (error) {
        console.error('Error confirming payment', error)
        const msg = 'Something went wrong confirming your payment. Please contact support.'
        toast(msg)
        // toast(() => <SnackBar isError title={msg} />, snackBar)
        this.root.transaction.setCheckoutState({
          isProcessing: false,
          buttonLabel: TransactionCTA.ERROR,
          status: TransactionStatus.ERROR
        })
      }
    } catch (error) {
      const msg = 'Failed to send transaction. Please try again or contact support.'
      // toast(() => <SnackBar isError title={error.message || msg} />, snackBar)
      this.root.transaction.setCheckoutState({
        isProcessing: false,
        buttonLabel: TransactionCTA.PAY,
        status: TransactionStatus.AWAITING_PAYMENT
      })
    }

    // Stop listening to polling events -> used to retrieve number of confirmations
    provider.off('poll')
  }

  async approveAllowance() {
    console.log('approveAllowance()')
    const provider = await this.root.wallets.resolveProvider()
    if (!provider) throw new Error('Unable to resolve provider')

    const order = this.root.transaction.order
    if (!order) throw new Error('No current transaction order found')

    const contract = this.root.transaction.contract
    if (!contract) throw new Error('No transfer estimate available')

    console.debug('Attempting approve allowances')

    this.root.transaction.setCheckoutState({
      isProcessing: true,
      buttonLabel: TransactionCTA.AWAITING_APPROVAL
    })
    try {
      const rawTxn = await contract.populateTransaction.approve(
        order.recipient,
        this.root.transaction.formattedTokens._hex
      )

      const transactionHash = await provider.send('eth_sendTransaction', [
        {
          from: rawTxn.from,
          to: rawTxn.to,
          data: rawTxn.data,
          value: rawTxn.value?._hex
        }
      ])

      this.root.transaction.setCheckoutState({
        isProcessing: true,
        isApprovingTokens: true,
        buttonLabel: TransactionCTA.APPROVING_TOKENS,
        status: TransactionStatus.APPROVING_TOKENS
      })

      await provider.waitForTransaction(transactionHash, 1)

      this.root.transaction.setCheckoutState({
        isProcessing: false,
        isApprovingTokens: false,
        hasRequiredAllowance: true,
        buttonLabel: TransactionCTA.PAY,
        status: TransactionStatus.AWAITING_PAYMENT
      })
    } catch (error) {
      const msg = 'Failed to send transaction. Please try again or contact support.'
      // toast(() => <SnackBar isError title={error.message || msg} />, snackBar)
      this.root.transaction.setCheckoutState({
        isProcessing: false,
        buttonLabel: TransactionCTA.APPROVE_TOKENS
      })
    }
  }

  async allowance(
    tokenSymbol: TokenSymbol,
    recipient: EthAddress,
    signer: EthAddress
  ): Promise<ethers.BigNumber> {
    const { token } = await this.getContractForToken(tokenSymbol)
    try {
      return token.allowance(signer, recipient)
    } catch (err) {
      console.log(err)
      return BigNumber.from(0)
    }
  }
}
